import { useEffect } from 'react';

/**
 * Pins `--app-height` to the web view's real height.
 *
 * The CSS fallback has to guess, and on iOS the guess is wrong. An installed
 * app whose `apple-mobile-web-app-status-bar-style` is `default` gets a web view
 * inset below the status bar - roughly 59pt on a Dynamic Island phone - but
 * `100vh` still reports the full glass. The shell then overruns the viewport by
 * that much, `body { overflow: hidden }` clips the overflow, and the bottom of
 * the nav goes over the edge: icons visible, labels gone.
 *
 * `window.innerHeight` is the measurement rather than the guess. It is also the
 * right signal in preference to `visualViewport.height`, which shrinks when the
 * keyboard opens - the shell should stay put behind a keyboard, not collapse.
 *
 * iOS caches that meta at "Add to Home Screen" time and never re-reads it, so
 * installs made before and after it changed behave differently forever. This
 * makes the shell correct under either.
 */
export function useAppHeight(): void {
  useEffect(() => {
    const apply = () => {
      document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`);
    };

    apply();

    // iOS reports the old height during a rotation, so re-measure once the new
    // layout has settled as well as immediately.
    const applyAfterSettle = () => {
      apply();
      requestAnimationFrame(apply);
      setTimeout(apply, 250);
    };

    window.addEventListener('resize', apply);
    window.addEventListener('orientationchange', applyAfterSettle);
    // Returning from the background can restore a stale height.
    window.addEventListener('pageshow', apply);

    return () => {
      window.removeEventListener('resize', apply);
      window.removeEventListener('orientationchange', applyAfterSettle);
      window.removeEventListener('pageshow', apply);
    };
  }, []);
}
